import React, { useState, useEffect } from 'react';
import { Head, usePage } from '@inertiajs/react';
import { useForm } from '@mantine/form';
import {
  Container,
  TextInput,
  Radio,
  RadioGroup,
  Textarea,
  Button,
  MantineProvider,
  createTheme,
  Box,
  Paper,
  Title,
  Accordion,
  Text,
  Group,
  MultiSelect,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { router } from '@inertiajs/react';

import { IconAlertCircle, IconCheck, IconLink } from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor, EditorContent } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import ImageUploadModal from '../../components/ImageUploadModal';
import { useUser, User } from '../../src/context/UserContext';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/Login-form.css';
import '@mantine/tiptap/styles.css';

interface EditArtistPageProps extends InertiaPageProps {
  artist: {
    id: number;
    name: string;
    slug: string;
    description?: string;
    website_url?: string;
    tiktok_url: string;
    instagram_url: string;
    youtube_url: string;
    spotify_url: string;
    facebook_url: string;
    soundcloud_url: string;
    mixcloud_url: string;
    dance_with_me: string;
    portal_password: string;
    short_bio: string;
    long_bio: string;
    promo_photos: string;
    promo_videos: string;
    traveler_info: string;
    preferred_airports: string;
    disliked_foods: string;
    favorite_foods: string;
    payment_methods: string;
    dance_style_ids: number[];
    rates: string;
    accomodation_info: string;
    stripe_account_id: string;
    is_portal_enabled?: boolean;
    private_lessons_enabled?: boolean;
    instructor?: boolean;
    dj?: boolean;
    avatar?: File | null;
    avatar_url?: string | null;
  };
  dance_styles: DanceStyle[];
  errors?: Record<string, string[]>;
}

interface DanceStyle {
  id: number;
  name: string;
}

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: { fontFamily: 'Poppins, sans-serif' },
});

export default function EditArtist() {
  const { artist, dance_styles, errors } = usePage<EditArtistPageProps>().props;
  console.log('Artist Data:', artist);
  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(artist.avatar_url);
  const { user, refreshUser } = useUser() as { user: User | null; refreshUser: () => Promise<void> };

  const form = useForm({
    initialValues: {
      name: artist.name || '',
      slug: artist.slug || '',
      instructor: artist.instructor ? 'true' : 'false',
      is_portal_enabled: artist.is_portal_enabled ? 'true' : 'false',
      private_lessons_enabled: artist.private_lessons_enabled ? 'true' : 'false',
      dj: artist.dj ? 'true' : 'false',
      description: artist.description || '',
      website_url: artist.website_url,
      facebook_url: artist.facebook_url,
      instagram_url: artist.instagram_url,
      youtube_url: artist.youtube_url,
      tiktok_url: artist.tiktok_url,
      spotify_url: artist.spotify_url,
      soundcloud_url: artist.soundcloud_url,
      mixcloud_url: artist.mixcloud_url,
      dance_with_me: artist.dance_with_me,
      portal_password: artist.portal_password,
      short_bio: artist.short_bio,
      long_bio: artist.long_bio,
      promo_photos: artist.promo_photos,
      promo_videos: artist.promo_videos,
      traveler_info: artist.traveler_info,
      preferred_airports: artist.preferred_airports,
      disliked_foods: artist.disliked_foods,
      favorite_foods: artist.favorite_foods,
      payment_methods: artist.payment_methods,
      rates: artist.rates,
      accomodation_info: artist.accomodation_info,
      stripe_account_id: artist.stripe_account_id,
      avatar_url: artist.avatar_url,
      avatar: null as File | null,
      dance_style_ids: artist.dance_style_ids || [],
    },
  });

  const handleImageChange = (file: File) => {
    // Update the form state with the new file
    form.setFieldValue('avatar', file);
    setSeoImagePreview(URL.createObjectURL(file));
    // Update the preview with a new URL
    console.log('Image changed - file stored in form:', file.name);
  };

  const [userRole, setUserRole] = useState(null);
  const is_superadmin = user?.roles?.includes('SuperAdmin') || false;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: artist.description,
    onUpdate: ({ editor }) => {
      form.setFieldValue('description', editor.getHTML());
    },
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: typeof form.values) => {
    console.log('📍 Submitting form with values:', values);
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    console.log('🔐 CSRF Token:', csrfToken);

    try {
      const formData = new FormData();

      // Add all basic artist fields
      formData.append('artist[name]', values.name);
      formData.append('artist[slug]', values.slug);
      formData.append('artist[description]', values.description);
      formData.append('artist[instructor]', values.instructor);
      formData.append('artist[dj]', values.dj);
      formData.append('artist[is_portal_enabled]', values.is_portal_enabled);
      formData.append('artist[private_lessons_enabled]', values.private_lessons_enabled);

      // Add dance style IDs if they exist
      if (values.dance_style_ids && values.dance_style_ids.length > 0) {
        values.dance_style_ids.forEach((id) => {
          formData.append('artist[dance_style_ids][]', id.toString());
        });
      }

      // Add all URL fields
      formData.append('artist[website_url]', values.website_url || '');
      formData.append('artist[facebook_url]', values.facebook_url || '');
      formData.append('artist[instagram_url]', values.instagram_url || '');
      formData.append('artist[youtube_url]', values.youtube_url || '');
      formData.append('artist[tiktok_url]', values.tiktok_url || '');
      formData.append('artist[spotify_url]', values.spotify_url || '');
      formData.append('artist[soundcloud_url]', values.soundcloud_url || '');
      formData.append('artist[mixcloud_url]', values.mixcloud_url || '');
      formData.append('artist[dance_with_me]', values.dance_with_me || '');

      // Add portal fields
      if (is_superadmin) {
        formData.append('artist[portal_password]', values.portal_password || '');
        formData.append('artist[short_bio]', values.short_bio || '');
        formData.append('artist[long_bio]', values.long_bio || '');
        formData.append('artist[promo_photos]', values.promo_photos || '');
        formData.append('artist[promo_videos]', values.promo_videos || '');
        formData.append('artist[traveler_info]', values.traveler_info || '');
        formData.append('artist[preferred_airports]', values.preferred_airports || '');
        formData.append('artist[disliked_foods]', values.disliked_foods || '');
        formData.append('artist[favorite_foods]', values.favorite_foods || '');
        formData.append('artist[payment_methods]', values.payment_methods || '');
        formData.append('artist[rates]', values.rates || '');
        formData.append('artist[accomodation_info]', values.accomodation_info || '');
        formData.append('artist[stripe_account_id]', values.stripe_account_id || '');
      }

      console.log('Form state for debugging:', form.values); // Check what's in the form

      // You should store the actual File object in your state
      // when handleImageChange is called
      const avatarFile: any = form.values.avatar; // This should be the File object

      if (avatarFile instanceof File) {
        console.log('✅ Found avatar File object:', avatarFile.name, avatarFile.size);
        formData.append('artist[avatar]', avatarFile);
      } else {
        console.log('❌ No valid avatar File object found in form state');
      }
      // Send PUT request with CSRF token
      const response = await fetch(`/artists/${artist.slug}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Update Successful',
          position: 'top-right',
          message: 'Artist was successfully updated!',
          icon: <IconCheck />,
          autoClose: 6000,
        });

        // Redirect to the updated artist page after success
        setTimeout(() => {
          window.location.href = `/artists/${values.slug}`;
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        showNotification({
          color: 'red',
          title: 'Update Failed',
          position: 'top-right',
          message: data.error || 'Something went wrong!',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });

        // Show validation errors if available
        if (data.errors) {
          form.setErrors(data.errors);
        }
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        position: 'top-right',
        message: 'An unexpected error occurred',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
      console.error('Error updating artist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="sm" style={{ minHeight: '75vh' }}>
        <Paper p="lg" shadow="xs" style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }} pos="relative">
          <Title order={3} style={{ color: '#FFF7ED' }} mb="sm">
            Edit Artist: {artist.name}
          </Title>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <ImageUploadModal previewUrl={seoImagePreview} onImageChange={handleImageChange} />

            <TextInput
              label="Artist Name"
              placeholder="Enter the name"
              {...form.getInputProps('name')}
              mt="md"
              styles={{ label: { color: '#FFF7ED' } }}
              required
            />

            <TextInput
              label="Slug"
              placeholder="your-event-slug"
              {...form.getInputProps('slug')}
              mt="md"
              mb="xl"
              styles={{ label: { color: '#FFF7ED' } }}
            />

            <MultiSelect
              label="Dance Styles"
              name="dance_style_ids"
              hidePickedOptions
              className="select-dropdown"
              styles={{
                label: { color: '#FFF7ED' },
                dropdown: { zIndex: 1000 },
              }}
              placeholder={
                form.values.dance_style_ids && form.values.dance_style_ids.length > 0 ? '' : 'Choose dance styles'
              }
              data={dance_styles.map((style) => ({
                value: style.id.toString(), // Convert to string for MultiSelect
                label: style.name,
              }))}
              value={form.values.dance_style_ids.map((id) => id.toString())} // Convert IDs to strings
              onChange={(values) => {
                // Convert the selected string IDs back to numbers
                const numericValues = values.map(Number);
                form.setFieldValue('dance_style_ids', numericValues);
              }}
              searchable
              clearable
              mt="lg"
            />

            <Text size="sm" fw={500} style={{ color: '#FFF7ED' }} mt="lg">
              Artist Description
            </Text>
            {editor && (
              <RichTextEditor editor={editor} className="rich-text-editor">
                <RichTextEditor.Toolbar>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                    <RichTextEditor.Strikethrough />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Code />
                    <RichTextEditor.Highlight />
                    <RichTextEditor.ClearFormatting />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.H1 />
                    <RichTextEditor.H2 />
                    <RichTextEditor.H3 />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Blockquote />
                    <RichTextEditor.Hr />
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Blockquote />
                    <RichTextEditor.OrderedList />
                    <RichTextEditor.BulletList />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Undo />
                    <RichTextEditor.Redo />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
              </RichTextEditor>
            )}

            <RadioGroup
              label="Instructor?"
              {...form.getInputProps('instructor')}
              mt="md"
              styles={{ label: { color: '#FFF7ED' } }}
            >
              <Radio value="false" label="Not an instructor" styles={{ label: { color: '#FFF7ED' } }} />
              <Radio value="true" label="Is an instructor" styles={{ label: { color: '#FFF7ED' } }} />
            </RadioGroup>

            <RadioGroup label="DJ?" {...form.getInputProps('dj')} mt="md" styles={{ label: { color: '#FFF7ED' } }}>
              <Radio value="false" label="Not a DJ" styles={{ label: { color: '#FFF7ED' } }} />
              <Radio value="true" label="Is A DJ" styles={{ label: { color: '#FFF7ED' } }} />
            </RadioGroup>

            <RadioGroup
              label="Is Portal Enabled?"
              {...form.getInputProps('is_portal_enabled')}
              mt="md"
              styles={{ label: { color: '#FFF7ED' } }}
            >
              <Radio value="false" label="Not enabled" styles={{ label: { color: '#FFF7ED' } }} />
              <Radio value="true" label="Is enabled" styles={{ label: { color: '#FFF7ED' } }} />
            </RadioGroup>

            <RadioGroup
              label="Are Private Lessons Enabled?"
              {...form.getInputProps('private_lessons_enabled')}
              mt="md"
              styles={{ label: { color: '#FFF7ED' } }}
            >
              <Radio value="false" label="Not enabled" styles={{ label: { color: '#FFF7ED' } }} />
              <Radio value="true" label="Is enabled" styles={{ label: { color: '#FFF7ED' } }} />
            </RadioGroup>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="website-links">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconLink size={20} />
                    <Text>Links</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <TextInput
                    label="Website Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the artist website."
                    {...form.getInputProps('website_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Facebook Page Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Facebook page."
                    {...form.getInputProps('facebook_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Instagram Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the artist Instagram page."
                    {...form.getInputProps('instagram_url')}
                    mt="md"
                  />

                  <TextInput
                    label="YouTube Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the YouTube Channel link."
                    {...form.getInputProps('youtube_url')}
                    mt="md"
                  />

                  <TextInput
                    label="TikTok Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the TikTok link."
                    {...form.getInputProps('tiktok_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Spotify Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the Spotify link."
                    {...form.getInputProps('spotify_url')}
                    mt="md"
                  />

                  <TextInput
                    label="SoundCloud Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the SoundCloud link."
                    {...form.getInputProps('soundcloud_url')}
                    mt="md"
                  />

                  <TextInput
                    label="MixCloud Link"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the MixCloud link."
                    {...form.getInputProps('mixcloud_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Dance With Me"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the Dance With Me link."
                    {...form.getInputProps('dance_with_me')}
                    mt="md"
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Button type="submit" mt="xl" size="lg" fullWidth className="mt-5">
              Update Artist
            </Button>
          </form>
        </Paper>
      </Container>
    </MantineProvider>
  );
}
